import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getCaseSummaryViaAI } from '../../../services/aiService';
import { referralService } from '../../../services/referralService';

const AISummaryModal = ({ referral, onClose, onRefresh }) => {
  const [summary, setSummary] = useState(referral?.urgency_summary || '');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const hasExistingSummary = !!referral?.urgency_summary;

  // Auto-generate summary on mount if none exists
  useEffect(() => {
    if (!hasExistingSummary) {
      handleGenerateSummary();
    }
  }, []);

  const handleGenerateSummary = async () => {
    try {
      setGenerating(true);
      setError('');
      setSuccess(false);

      // Generate AI summary using Edge Function (secured with JWT)
      const generatedSummary = await getCaseSummaryViaAI(referral?.id);
      
      if (!generatedSummary) {
        throw new Error('Aucun résumé généré');
      }

      // Update database with new summary
      await referralService?.updateSummary(referral?.id, generatedSummary);
      
      // Update local state immediately
      setSummary(generatedSummary);
      setSuccess(true);
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error generating summary:', err);
      setError(err?.message || 'Erreur lors de la génération du résumé');
    } finally {
      setGenerating(false);
      // Refresh parent data silently after generation completes
      if (onRefresh) {
        await onRefresh();
      }
    }
  };

  const handleRegenerateSummary = async () => {
    if (window.confirm('Voulez-vous vraiment régénérer le résumé IA ? Le résumé actuel sera remplacé.')) {
      await handleGenerateSummary();
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60]"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div
          className="bg-card rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-y-auto shadow-2xl border border-white/8"
          onClick={(e) => e?.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-card/95 backdrop-blur-xl border-b border-white/8 p-6 flex items-center justify-between z-10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center border border-purple-500/30">
                <Icon name="Sparkles" size={20} className="text-purple-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-primary">
                  Résumé IA du cas
                </h2>
                <p className="text-sm text-muted mt-0.5">
                  {referral?.patientFirstName} {referral?.patientLastName}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-xl bg-card-elevated hover:bg-card-hover flex items-center justify-center transition-colors border border-white/8"
            >
              <Icon name="X" size={20} className="text-secondary" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Loading State */}
            {generating && !summary && (
              <div className="bg-card-elevated border border-white/8 rounded-xl p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-purple-500/10 flex items-center justify-center mx-auto mb-4 border border-purple-500/20 animate-pulse">
                  <Icon name="Sparkles" size={32} className="text-purple-400 animate-spin" />
                </div>
                <h3 className="text-lg font-semibold text-primary mb-2">
                  Génération du résumé en cours...
                </h3>
                <p className="text-sm text-muted">
                  L'IA analyse les raisons d'adressage, notes cliniques et données dentaires
                </p>
              </div>
            )}

            {/* Clinical Data Sections - Similar to Details Modal */}
            {!generating && (
              <>
                {/* Referral Reasons Section */}
                <div className="bg-card-elevated border border-white/8 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Icon name="FileText" size={18} className="text-accent" />
                    <h3 className="text-base font-semibold text-primary">Raisons de l'adressage</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {referral?.referralReasons?.map((reason, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-3 py-1.5 rounded-lg bg-accent/10 text-accent text-sm font-medium border border-accent/20"
                      >
                        {reason}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Clinical Notes Section */}
                {referral?.clinicalNotes && (
                  <div className="bg-card-elevated border border-white/8 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Icon name="Stethoscope" size={18} className="text-secondary" />
                      <h3 className="text-base font-semibold text-primary">Notes cliniques</h3>
                    </div>
                    <p className="text-sm text-secondary leading-relaxed whitespace-pre-wrap">
                      {referral?.clinicalNotes}
                    </p>
                  </div>
                )}

                {/* Tooth Numbers Section */}
                <div className="bg-card-elevated border border-white/8 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="Activity" size={18} className="text-primary" />
                    <h3 className="text-base font-semibold text-primary">Dent(s) concernée(s)</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {referral?.toothNumbers?.map((tooth, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 text-primary text-base font-bold border border-primary/20"
                      >
                        {tooth}
                      </span>
                    ))}
                  </div>
                </div>

                {/* AI Summary Display */}
                {summary && (
                  <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/30 rounded-xl p-6">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center border border-purple-500/30 flex-shrink-0">
                        <Icon name="Sparkles" size={20} className="text-purple-400" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="text-base font-semibold text-purple-300">Résumé généré par IA</h3>
                          {hasExistingSummary && (
                            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-success/20 border border-success/30">
                              <Icon name="CheckCircle" size={12} className="text-success" />
                              <span className="text-[10px] font-semibold text-success">Enregistré</span>
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-muted">Analyse complète par OpenAI</p>
                      </div>
                    </div>
                    <div className="bg-card/60 rounded-lg p-5 border border-purple-500/20">
                      <p className="text-sm text-secondary leading-relaxed whitespace-pre-wrap">
                        {summary}
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-error/10 border border-error/30 rounded-xl p-4 flex items-start gap-3">
                <Icon name="AlertCircle" size={18} className="text-error mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-error mb-1">Erreur</p>
                  <p className="text-xs text-error/80">{error}</p>
                </div>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="bg-success/10 border border-success/30 rounded-xl p-4 flex items-center gap-3">
                <Icon name="CheckCircle" size={18} className="text-success" />
                <p className="text-sm font-semibold text-success">Résumé généré et enregistré avec succès</p>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="sticky bottom-0 bg-card/95 backdrop-blur-xl border-t border-white/8 p-6 flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={onClose}
            >
              Fermer
            </Button>
            <div className="flex items-center gap-3">
              {summary && (
                <Button
                  variant="outline"
                  onClick={handleRegenerateSummary}
                  disabled={generating}
                  iconName="RefreshCw"
                >
                  Régénérer
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AISummaryModal;